<template>
  <div class="chart-stacked-bars">
    <div class="chart-stacked-bars-chart">
      <Bar :data="data" :options="data.options" :plugins="[ChartDataLabels]" />
    </div>
    <div class="chart-legend">
      <p class="chart-legend-title"> {{ title }} <span v-if="total">{{ getTotal() }} tonnes</span></p>
      <div class="chart-legend-labels">
        <p v-for="(ds, i) in props.datasets" :key="i" :class="`chart-legend-label chart-legend-label--${ds.class}`">
          {{ ds.label }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Bar } from 'vue-chartjs'
import { Chart, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { defineProps } from 'vue'
import ChartDataLabels from 'chartjs-plugin-datalabels'

const props = defineProps<{
  datasets: { label: string, backgroundColor: string, data: number[], class: string }[]
  total?: boolean

}>()

Chart.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)
Chart.defaults.datasets.bar.barPercentage = 1.0
Chart.defaults.datasets.bar.categoryPercentage = 0.5


const data = {
  labels: [''],
  datasets: props.datasets,
  options: {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        align: 'center' as const,
        anchor: 'center' as const,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 18,
        },
        borderRadius: 3,
        padding: 4,
        display: (context: any) => context.dataset.data[context.dataIndex] !== 0,
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#0072ce',
          font: {
            weight: 700,
          },
        },
      },
      y: {
        border: {
          display: false,
        },
        stacked: true,
        grid: {
          color: '#0072ce',
        },
        ticks: {
          color: '#0072ce',
          padding: 20,
          stepSize: 1,
          font: {
            weight: 700,
            size: 14,
          },
        },
        title: {
          display: true,
          text: 'Tonnes',
          color: '#0072ce',
          font: {
            weight: 700,
          },
        },
      },
    },
  },
}

const title = new Date(new Date().getFullYear(), new Date().getMonth()).toLocaleString('fr-FR', { month: 'long' });

function getTotal() {
  return props.datasets.reduce((total, ds) => total + ds.data[0], 0).toFixed(2) 
}

</script>

<style scoped lang="scss">
.chart-stacked-bars {
  display: flex;
  height: 100%;
  gap: var(--spacer);

  .chart-legend {
    &-title {
      max-width: fit-content;
      text-transform: uppercase;
      padding: 5px;
      background-color: var(--vc-primary);
      color: var(--vc-white);
      font-size: 30px;
      font-weight: 700;
      line-height: 1;
      position: relative;
      margin-bottom: var(--spacer-2);

      &::after {
        content: '';
        position: absolute;
        width: 20px;
        height: 20px;
        display: block;
        background-color: var(--vc-primary);
        bottom: -10px;
        left: 20px;
        transform: rotate(45deg);
        z-index: -1;
      }
    }

    &-label {
      display: flex;
      padding-left: var(--spacer-2);
      position: relative;
      align-items: flex-start;
      margin-bottom: var(--spacer);
      text-transform: uppercase;
      line-height: 1.2;
      font-size: 20px;

      &::before {
        content: '';
        position: absolute;
        width: 20px;
        height: 20px;
        display: block;
        background-color: var(--vc-primary);
        // bottom: -10px;
        left: 0;
        border-radius: 100px;
      }

      &--glass {
        color: var(--vc-gray4-0_8);

        &::before {
          background-color: var(--vc-gray4-0_8);
        }
      }

      &--paper {
        color: var(--vc-primary-0_8);

        &::before {
          background-color: var(--vc-primary-0_8);
        }
      }

      &--recyclable {
        color: var(--vc-green-0_8);

        &::before {
          background-color: var(--vc-green-0_8);
        }
      }

      &--mix {
        color: var(--vc-primary);

        &::before {
          background-color: var(--vc-primary);
        }
      }

      &--organic {
        color: var(--vc-green);

        &::before {
          background-color: var(--vc-green);
        }
      }
    }
  }
}
</style>
